//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type ElementType } from 'react';
import {
  Block, Cancel, CheckCircle, Error, HelpOutlined, Pause, PlayArrow,
  Schedule, Undo
} from '@mui/icons-material';

import { type JobStatus } from '@/services/apiTypes';

export interface JobStatusDetail {
  readonly display: string;
  readonly icon: ElementType;
  readonly description: string;
}

// Single source of truth for per-status presentation. Every surface that
// renders a job status (Jobs page, dashboard widgets, Explorer) derives
// from this map so a new backend status only needs to be added here and
// to the JobStatus union.
export const JOB_STATUS_DETAILS: Record<JobStatus, JobStatusDetail> = {
  scheduled: {
    display: 'scheduled',
    icon: Schedule,
    description: 'Scheduled for execution'
  },
  running: {
    display: 'running',
    icon: PlayArrow,
    description: 'Currently executing'
  },
  suspended: {
    display: 'suspended',
    icon: Pause,
    description: 'Suspended by the scheduler'
  },
  blocked: {
    display: 'blocked',
    icon: Block,
    description: 'Waiting on a resource conflict'
  },
  completed: {
    display: 'completed',
    icon: CheckCircle,
    description: 'Successfully completed'
  },
  canceled: {
    display: 'canceled',
    icon: Cancel,
    description: 'Canceled'
  },
  failed: {
    display: 'failed',
    icon: Error,
    description: 'Execution failed'
  },
  rollback: {
    display: 'rollback',
    icon: Undo,
    description: 'Rollbacks due to conflict'
  },
  unknown_job_status: {
    display: 'unknown',
    icon: HelpOutlined,
    description: 'Status unknown'
  }
};

// Display order for pills, charts, and other status enumerations.
export const JOB_STATUSES =
  Object.keys(JOB_STATUS_DETAILS) as readonly JobStatus[];

// Jobs whose runtime/status can still change: cancellable, refreshed by
// live polling, and ticking in Live mode.
export const ACTIVE_STATUSES: ReadonlySet<JobStatus> =
  new Set(['running', 'scheduled', 'suspended', 'blocked']);

// Jobs actually executing on the engine, for which an in-progress bar is
// meaningful.  Excludes 'scheduled': a queued job hasn't started, so it has
// no progress to show (an indeterminate bar would misrepresent it as running).
export const EXECUTING_STATUSES: ReadonlySet<JobStatus> =
  new Set(['running', 'suspended', 'blocked']);

export const isActiveStatus = (status: JobStatus): boolean =>
  ACTIVE_STATUSES.has(status);
